/* eslint-disable max-len */
/* eslint-disable no-param-reassign */
import React, { useCallback, useState, useEffect } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
// eslint-disable-next-line import/no-unresolved
import { MetadataContent } from '@jutro/legacy/uiconfig';
import { useTranslator } from '@jutro/locale';
import { MLTooltipComponent } from 'ml-components-platform-react';
import MLPAUMBCoveragesComponent from '../MLPAUMBCoveragesComponent/MLPAUMBCoveragesComponent';
import MLEditableCoveragesComponent from '../MLEditableCoveragesComponent/MLEditableCoveragesComponent';
import MLReadOnlyCoverageComponent from '../MLReadOnlyCoverageComponent/MLReadOnlyCoverageComponent';
import MLPAScrollingTableHeaderComponent from '../MLPAScrollingTableHeaderComponent/MLPAScrollingTableHeaderComponent';
import MLPAEditableCoveragesComponent from '../MLPAEditableCoveragesComponent/MLPAEditableCoveragesComponent';
import Coverage from '../Models/Coverage';
import '@xengage/gw-platform-translations';
import metadata from './MLPAAgentEditCoveragesComponent.metadata.json5';
import styles from './MLPAAgentEditCoveragesComponent.module.scss';
import messages from './MLPAAgentEditCoveragesComponent.messages';

const ACTION_ADD_VEHICLE = 'add';
const VEHICLE_STEP = '/vehicles';

function TermRepeatComponent(props) {
    const {
        terms,
    } = props;
    return (
        <>    
            {terms?.map((term) => {
                return (
                    <li> 
                        {term.isVisible_ML && (
                            <div className={styles['term-name']}>
                                {_.isNil(term.name) ? term.termName : term.name}
                            </div>
                        )}
                    </li>);
            })}
        </>
    );
}

function MLPAAgentEditCoveragesComponent(props) {
    const {
        coverages,
        loading,
        onCovChange,
        onGeneralChange,
        addVehicleButton,
        isDisplayOnly,
        isAlaska7Day,
        termMonths,
        quoteStatus,
        itemizedFees,
        totalFees,
        prevIndex,
        nextIndex,
        isPrevAvailable,
        isNextAvailable,
        lineCoverageNamesMap,
        vehicleCoverageNamesMap,
        optionalCoverageNamesMap,
        coverablesToDisplay,
        updateMaps,
        onPrevButtonClick,
        onNextButtonClick,
        navigateToStep,
        isBundle,
        filterData,
        isPolicyChange,
        hasNamedNonOwner,
        isRewrite,
        isModernProduct,
        submissionVM
    } = props;

    const [model, setModel] = useState([]);

    // Wrapper function as coverage object getter needs a function
    const isLoading = useCallback(() => {
        return loading;
    }, [loading]);


    useEffect(() => {
        if (!coverages || _.isEmpty(coverages)) {
            return;
        }
        //  Loop over coverages and convert into coverage objects. Don't convert vehicle coverages as they have different stucture
        const updatedModel = {};
        for (const [coverageType, coveragesList] of Object.entries(coverages)) {
            if (coverageType === 'vehicleCoverages') {
                updatedModel[coverageType] = coveragesList;
            } else {
                updatedModel[coverageType] = coveragesList.map(_.partial(Coverage.create, _, isLoading, onCovChange));
                updatedModel[coverageType] = _.sortBy(updatedModel[coverageType].filter((cov) => cov !== undefined), 'name');
            }
        }
        setModel(updatedModel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coverages]);

    useEffect(() => {
        updateMaps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coverages.vehicleCoverages]);


    const translator = useTranslator();
    const [displayedLineCoverages, setDisplayedLineCoverages] = useState(new Map());

    const isNamedNonOwner = useCallback(() => {
        return hasNamedNonOwner || _.get(model, 'vehicleCoverages', []).some((vehicle) => {
            return vehicle.vehicleName === 'Named Non-Owner';
        });
    }, [model]);

    const coverageSelectedChanged = useCallback((isSelected, coverage) => {
        coverage.selected = isSelected;
    }, []);

    const formatVehicleCoverages = useCallback((vehicle) => {
        const baseVehicleCovs = _.get(vehicle, 'syncedCoverages');
        const wrapperCovs = baseVehicleCovs.map(_.partial(Coverage.create, _, isLoading, onCovChange));
        return _.sortBy(wrapperCovs, 'name');
    }, [isLoading, onCovChange]);

    const onAddVehicle = useCallback(() => {
        const data = {
            action: ACTION_ADD_VEHICLE
        };
        const stepName = VEHICLE_STEP;
        navigateToStep(stepName, data);
    }, [navigateToStep]);

    const generateLineCovOverrides = useCallback(() => {
        const lineCovs = _.filter(_.get(model, 'lineCoverages', []), filterData);
        const lineCovOverrides = lineCovs.map((cov, index) => {
            let firstPartyBenifitsTerms;
            if (cov.name === 'First Party Benefits') {
                firstPartyBenifitsTerms = cov.terms.filter((obj, i) => {
                    return i === cov.terms.findIndex((o) => obj.name === o.name);
                });
                if (firstPartyBenifitsTerms.length > 5) {
                    firstPartyBenifitsTerms.pop();
                }
            }

            return {
                [`lineCovLi${index}`]: {
                    className: index === 0 ? 'divider' : ''
                },
                [`lineCovRequired${index}`]: {
                    visible: cov.required || isDisplayOnly
                },
                [`lineCovNonRequired${index}`]: {
                    visible: !cov.required && !isDisplayOnly
                },
                [`lineCoverageRequiredGreenCheck${index}`]: {
                    visible: cov.required
                },
                [`lineCoverageRequiredName${index}`]: {
                    content: cov.name
                },
                [`lineCoverageRequiredTooltip${index}`]: {
                    tooltipContent: cov.description
                },
                [`lineSelectedCheckBox${index}`]: {
                    label: cov.name,
                    tooltip: {
                        text: cov.description
                    },
                    onValueChange: coverageSelectedChanged,
                    path: cov,
                    value: cov.selected
                },
                [`lineLiTermRepeat${index}`]: {
                    visible: !_.isEmpty(cov.terms),
                    terms: cov.name === 'First Party Benefits' ? firstPartyBenifitsTerms : cov.terms
                }
            };
        });
        return Object.assign({}, ...lineCovOverrides);
    }, [coverageSelectedChanged, filterData, isDisplayOnly, model]);

    const generateVehicleCovOverrides = useCallback(() => {
        const vehicleCovOverrides = _.filter(vehicleCoverageNamesMap, filterData).map((cov, index) => {
            return {
                [`vehCovLi${index}`]: {
                    className: index === 0 ? 'divider' : ''
                },
                [`vehRequiredIcon${index}`]: {
                    visible: cov.required
                },
                [`vehCoverageDescTooltip${index}`]: {
                    tooltipContent: cov.description
                },
                [`vehLiTermRepeat${index}`]: {
                    visible: !_.isEmpty(cov.terms),
                    terms: cov.terms
                }
            };
        });

        return Object.assign({}, ...vehicleCovOverrides);
    }, [filterData, vehicleCoverageNamesMap]);

    const generateOptCovOverrides = useCallback(() => {
        let optCovs = _.filter(_.get(model, 'optionalCoverages_ML', []), filterData);
        
        if (isRewrite) {
            optCovs = optCovs.filter((cov) => {
                    return cov.selected
            })
        }

        const optCovOverrides = optCovs.map((cov, index) => {        
            return {
                [`optCovLi${index}`]: {
                    className: index === 0 ? 'divider' : ''
                },
                [`optRequiredIcon${index}`]: {
                    visible: cov.required
                },
                [`optCoverageDescTooltip${index}`]: {
                    tooltipContent: cov.description
                },
                [`optLiTermRepeat${index}`]: {
                    visible: !_.isEmpty(cov.terms),
                    terms: cov.terms
                },
                [`optionalCovCheckboxContainer${index}`]: {
                    visible: cov.name === "Limited Mexico Coverage" || cov.name === "Accident Forgiveness" || cov.name === "Declining Deductible" || cov.name === "Regular Use of Cars Owned by Others" || cov.name === "Diminishing Deductible" || cov.name === "Auto Extra" || cov.name === "Travel Package" || cov.name === "Local Delivery Coverage"|| cov.name === "Ride Share Coverage" || cov.name === "Worldwide Liability" || cov.name === "Named Non-Owner Coverage" || cov.name === "Non-Owner",
                },
                [`editableCheckboxOptionalCov${index}`]: {
                    label: cov.name,
                    hideLabel: true,
                    visible:  cov.name === "Limited Mexico Coverage" || cov.name === "Accident Forgiveness" || cov.name === "Declining Deductible" || cov.name === "Regular Use of Cars Owned by Others" || cov.name === "Diminishing Deductible" || cov.name === "Auto Extra" || cov.name === "Travel Package" || cov.name === "Local Delivery Coverage"|| cov.name === "Ride Share Coverage" || cov.name === "Worldwide Liability" || cov.name === "Named Non-Owner Coverage" || cov.name === "Non-Owner",
                    disabled: cov.required || loading || isDisplayOnly || _.isEmpty(cov),
                    onValueChange: coverageSelectedChanged,                   
                    path: cov,
                    value: cov.selected
                },
            };
        });

        return Object.assign({}, ...optCovOverrides);
    }, [filterData, optionalCoverageNamesMap]);

    const generateCoverablesOverrides = useCallback(() => {
        const vehicleOverrides = coverablesToDisplay.map((vehicle, index) => {
            return {
                [`paEditableLineCoverages${index}`]: {
                    model: _.get(model, 'lineCoverages'),
                    loading,
                    onCovChange: onCovChange,
                    allowSelectCoverage: true,
                    isDisplayOnly,
                    level: 'policy',
                    vehicle,
                    filterData,
                    displayedLineCoverages,
                    vehiclesLength : _.get(model, 'vehicleCoverages.length'),
                    isModernProduct,
                    submissionVM
                },
                [`paEditableSyncedCoverages${index}`]: {
                    model: formatVehicleCoverages(vehicle),
                    loading,
                    onCovChange: onCovChange,
                    allowSelectCoverage: true,
                    isDisplayOnly,
                    vehicle,
                    filterData,
                    level: 'vehicle',
                    isRewrite,
                    namesMap: vehicleCoverageNamesMap,
                    vehiclesLength : _.get(model, 'vehicleCoverages.length'),
                    isModernProduct,
                    submissionVM
                },
                [`paEditableOptCoverages${index}`]: {
                    model: _.get(model, 'optionalCoverages_ML'),
                    loading,
                    onCovChange: onCovChange,
                    allowSelectCoverage: true,
                    isDisplayOnly,
                    filterData,
                    level: 'vehicle',
                    visible: optionalCoverageNamesMap.length > 0,
                    isRewrite,
                    namesMap: optionalCoverageNamesMap,
                    vehiclesLength : _.get(model, 'vehicleCoverages.length'),
                    vehicle,
                    isModernProduct,
                    submissionVM
                }
            };
        });

        return Object.assign({}, ...vehicleOverrides);
    }, [coverablesToDisplay, displayedLineCoverages, filterData, formatVehicleCoverages, isDisplayOnly, loading, model, onCovChange]);

    const overrides = {
        editCoveragesBaseContainer: {
            className: isDisplayOnly ? 'ml-pa-edit-coverages display-only' : 'ml-pa-edit-coverages'
        },
        scrollingTableHeaderLeft: {
            className: isDisplayOnly ? 'ml-scrolling-table__header editing' : 'ml-scrolling-table__header'
        },
        leftSideHeaderH1: {
            content: isNamedNonOwner() ? translator(messages.coverages) : (
                <>
                    {translator(messages.vehicles)}
                    <span className="ml-pill ml-pill-default-inverse large">
                        {_.get(model, 'vehicleCoverages.length')}
                    </span>
                </>
                
            )
        },
        stateFeesComponent: {
            visible: _.get(totalFees, 'amount', 0) > 0,
            totalFees: totalFees,
            itemizedFees: itemizedFees,
            displayAlternativeTemplate: true
        },
        headerLi: {
            content: isNamedNonOwner() ? translator(messages.coverageTotal) : translator(messages.vehicleTotal)
        },
        lineCovsDivIterable: {
            data: _.filter(_.get(model, 'lineCoverages', []), filterData)
        },
        vehCovsDivIterable: {
            data: _.filter(vehicleCoverageNamesMap, filterData)
        },
        optCovsDivIterable: {
            data: _.filter(optionalCoverageNamesMap, filterData)
        },
        scrollingHeaderComponent: {
            model,
            isDisplayOnly,
            isAlaska7Day,
            termMonths,
            quoteStatus,
            hasNamedNonOwner : isNamedNonOwner(),
            isPolicyChange,
            prevIndex,
            nextIndex,
            isPrevAvailable,
            isNextAvailable,
            coverablesToDisplay,
            updateMaps,
            onPrevButtonClick,
            onNextButtonClick,
            navigateToStep,
            filterData,
            isModernProduct
        },
        coverablesToDisplayIterable: {
            data: coverablesToDisplay
        },
        addVehicleDiv: {
            visible: !!addVehicleButton && !isDisplayOnly
        },
        ...generateLineCovOverrides(),
        ...generateVehicleCovOverrides(),
        ...generateOptCovOverrides(),
        ...generateCoverablesOverrides()
    };

    const resolvers = {
        resolveCallbackMap: {
            onAddVehicle
        },
        resolveComponentMap: {
            mlpaumbcoveragescomponent: MLPAUMBCoveragesComponent,
            mleditablecoveragescomponent: MLEditableCoveragesComponent,
            mlreadonlycoveragecomponent: MLReadOnlyCoverageComponent,
            mlpascrollingtableheadercomponent: MLPAScrollingTableHeaderComponent,
            mlpaeditablecoveragescomponent: MLPAEditableCoveragesComponent,
            termrepeatcomponent: TermRepeatComponent,
            mltooltipcomponent: MLTooltipComponent
        },
        resolveClassNameMap: styles
    };

    return (
        <MetadataContent
            uiProps={metadata.componentContent}
            overrideProps={overrides}
            {...resolvers} />
    );
}

MLPAAgentEditCoveragesComponent.propTypes = {
    coverages: PropTypes.shape({}),
    onCovChange: PropTypes.func,
    loading: PropTypes.bool,
    onGeneralChange: PropTypes.func,
    isDisplayOnly: PropTypes.bool,
    addVehicleButton: PropTypes.bool,
    isAlaska7Day: PropTypes.bool,
    termMonths: PropTypes.number,
    quoteStatus: PropTypes.shape({}),
    itemizedFees: PropTypes.arrayOf(PropTypes.shape({})),
    totalFees: PropTypes.shape({}),
    prevIndex: PropTypes.number,
    nextIndex: PropTypes.number,
    isPrevAvailable: PropTypes.func,
    isNextAvailable: PropTypes.number,
    lineCoverageNamesMap: PropTypes.arrayOf(PropTypes.shape({})),
    vehicleCoverageNamesMap: PropTypes.arrayOf(PropTypes.shape({})),
    optionalCoverageNamesMap: PropTypes.arrayOf(PropTypes.shape({})),
    coverablesToDisplay: PropTypes.arrayOf(PropTypes.shape({})),
    updateMaps: PropTypes.func,
    onPrevButtonClick: PropTypes.func,
    onNextButtonClick: PropTypes.func,
    navigateToStep: PropTypes.func,
    isBundle: PropTypes.bool,
    isPolicyChange: PropTypes.bool,
    hasNamedNonOwner: PropTypes.bool
};

export default MLPAAgentEditCoveragesComponent;
